/* tutor-1 (C): EL PINTOR DE LA RESPUESTA DEL TUTOR.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   No hay biblioteca de Markdown en el repo y el chat del profe pinta texto plano, asi que el tutor trae su pieza
   chica. Entiende, y nada mas: parrafos; listas numeradas (`1.`) con el numero en circulo naranja suave; listas con
   vineta (`-` o `*`); negritas `**x**`; codigo en linea con comillas invertidas; un renglon que empieza con `> ` es el
   recuadro naranja suave de la regla; uno que empieza con `NOTE: ` es el renglon gris con el icono de informacion
   (el marcador se quita); las formulas las pinta `MathText` (`\(...\)` en linea; `$$...$$` en su tarjeta interior).
   El primer parrafo va con peso 500. Nada de HTML crudo: todo texto pasa por React.

   tutor-2 (E.1, #670): el bloque de un renglon con tres comillas invertidas y `svg`, el SVG y un renglon con las tres
   comillas, es un DIAGRAMA: se pinta como IMAGEN (`<img>` con un `data:` del SVG), nunca como codigo vivo, en una
   tarjeta interior como la de las formulas, con el `<title>` como `alt`. Antes de pintar pasa el MISMO saneado que el
   servidor (`motivoDelSvg`, con `DOMParser` en modo `image/svg+xml`): lo que no pasa no se pinta (nada en su
   lugar). Mientras llega en vivo y todavia no cierra, no se pinta nada de el. */
import React, { Suspense } from 'react';
import { Info } from 'lucide-react';

// KaTeX viaja SOLO en el pedazo de quien lo usa (la invariante de App.js): el carril de la app importa esta pagina
// directo, asi que aqui `MathText` se pide en diferido y, mientras llega, se ve el texto tal cual.
const MathText = React.lazy(() => import('../MathText'));

const NUMERADA = /^\s*(\d{1,3})[.)]\s+(.*)$/;
const VINETA = /^\s*[-*]\s+(.*)$/;
const REGLA = /^\s*>\s?(.*)$/;
const NOTA = /^\s*NOTE:\s*(.*)$/;

/** Las citas que la busqueda mete en el texto ("([dominio](url))") salen enteras; una liga suelta deja su texto.
 *  El servidor ya las quita del texto guardado; esto cubre lo que llega en vivo. */
export function quitarCitas(texto) {
  return String(texto || '').replace(/\s*\(\[[^\]\n]{1,200}\]\(https?:\/\/[^\s)]+\)\)/g, '').replace(/\[([^\]\n]{1,200})\]\(https?:\/\/[^\s)]+\)/g, '$1');
}

export const TOPE_LETRAS_SVG = 30000;
const PROHIBIDAS = ['script', 'foreignobject', 'image'];
const nombreLocal = (n) => String(n || '').split(':').pop().toLowerCase();

/** null si el SVG pasa; si no, el motivo. Las MISMAS reglas que `services/tutor/modelo.py::motivo_svg`. */
export function motivoDelSvg(svg) {
  const texto = String(svg || '');
  if (texto.length > TOPE_LETRAS_SVG) return 'largo';
  if (/<!DOCTYPE|<!ENTITY/i.test(texto)) return 'no_parsea';
  let raiz;
  try {
    const doc = new DOMParser().parseFromString(texto, 'image/svg+xml');
    if (doc.getElementsByTagName('parsererror').length) return 'no_parsea';
    raiz = doc.documentElement;
  } catch (e) {
    return 'no_parsea';
  }
  if (!raiz || nombreLocal(raiz.localName || raiz.nodeName) !== 'svg') return 'no_parsea';
  if (!Array.from(raiz.attributes).some((a) => nombreLocal(a.localName || a.name) === 'viewbox')) return 'sin_viewbox';
  const elementos = [raiz, ...Array.from(raiz.getElementsByTagName('*'))];
  for (const el of elementos) {
    const nombre = nombreLocal(el.localName || el.nodeName);
    if (PROHIBIDAS.includes(nombre)) return nombre;
    if (nombre === 'style' && /url\(/i.test(el.textContent || '')) return 'style_url';
    for (const a of Array.from(el.attributes)) {
      const atributo = nombreLocal(a.localName || a.name);
      const valor = String(a.value || '').trim();
      if (atributo.startsWith('on')) return 'evento_on';
      if (atributo === 'href' && !valor.startsWith('#')) return nombre === 'use' ? 'use_externo' : 'href_externo';
      if (atributo === 'style' && /url\(/i.test(valor)) return 'style_url';
    }
  }
  return null;
}

/** Lo que viaja dentro del `data:`. Un SVG pintado como IMAGEN no se ve sin su espacio de nombres, y el modelo casi
 *  nunca lo escribe (medido en el banco: 4 de 4 sin el): si falta, se le pone; nada mas cambia. */
export function svgParaPintar(svg) {
  const texto = String(svg || '');
  const apertura = /<svg\b[^>]*>/i.exec(texto);
  if (!apertura || /\sxmlns\s*=/.test(apertura[0])) return texto;
  return texto.replace(/<svg\b/i, '<svg xmlns="http://www.w3.org/2000/svg"');
}

/** El `<title>` del SVG (su texto), o '' si no lo trae. */
export function tituloDelSvg(svg) {
  try {
    const doc = new DOMParser().parseFromString(String(svg || ''), 'image/svg+xml');
    const titulo = Array.from(doc.getElementsByTagName('*')).find((n) => nombreLocal(n.localName || n.nodeName) === 'title');
    return titulo ? String(titulo.textContent || '').trim() : '';
  } catch (e) {
    return '';
  }
}

/** Parte el texto en bloques: `{tipo: parrafo|numerada|vinetas|regla|nota|formula|diagrama, ...}`. */
export function bloquesDe(texto) {
  const renglones = quitarCitas(texto).replace(/\r\n/g, '\n').split('\n');
  const bloques = [];
  let parrafo = [];
  const cerrarParrafo = () => {
    if (parrafo.length) bloques.push({ tipo: 'parrafo', texto: parrafo.join(' ') });
    parrafo = [];
  };
  for (let i = 0; i < renglones.length; i += 1) {
    const r = renglones[i];
    const limpio = r.trim();
    if (!limpio) {
      cerrarParrafo();
      continue;
    }
    // tutor-2 (E.1): el diagrama, solo si ya cerro; abierto (llegando en vivo) no se pinta nada de el
    if (limpio === '```svg') {
      cerrarParrafo();
      const cuerpo = [];
      let j = i + 1;
      while (j < renglones.length && renglones[j].trim() !== '```') { cuerpo.push(renglones[j]); j += 1; }
      if (j >= renglones.length) break;
      bloques.push({ tipo: 'diagrama', svg: cuerpo.join('\n') });
      i = j;
      continue;
    }
    // la formula en su renglon: `$$...$$` o `\[...\]` (los dos los pinta `MathText` como display)
    const cierre = limpio.startsWith('$$') ? '$$' : (limpio.startsWith('\\[') ? '\\]' : null);
    if (cierre) {
      cerrarParrafo();
      const partes = [limpio];
      const cerrada = limpio.length > 2 && limpio.endsWith(cierre);
      while (!cerrada && i + 1 < renglones.length) {
        i += 1;
        partes.push(renglones[i].trim());
        if (renglones[i].trim().endsWith(cierre)) break;
      }
      bloques.push({ tipo: 'formula', texto: partes.join(' ') });
      continue;
    }
    let m = NUMERADA.exec(r);
    if (m) {
      cerrarParrafo();
      const ultimo = bloques[bloques.length - 1];
      if (ultimo && ultimo.tipo === 'numerada') ultimo.items.push(m[2]);
      else bloques.push({ tipo: 'numerada', items: [m[2]] });
      continue;
    }
    m = VINETA.exec(r);
    if (m) {
      cerrarParrafo();
      const ultimo = bloques[bloques.length - 1];
      if (ultimo && ultimo.tipo === 'vinetas') ultimo.items.push(m[1]);
      else bloques.push({ tipo: 'vinetas', items: [m[1]] });
      continue;
    }
    m = REGLA.exec(r);
    if (m) {
      cerrarParrafo();
      const ultimo = bloques[bloques.length - 1];
      if (ultimo && ultimo.tipo === 'regla') ultimo.texto += ` ${m[1]}`;
      else bloques.push({ tipo: 'regla', texto: m[1] });
      continue;
    }
    m = NOTA.exec(r);
    if (m) {
      cerrarParrafo();
      bloques.push({ tipo: 'nota', texto: m[1] });
      continue;
    }
    parrafo.push(limpio);
  }
  cerrarParrafo();
  return bloques;
}

/** Lo de dentro de un renglon: negritas, codigo y formulas en linea. */
export function EnLinea({ texto }) {
  const piezas = [];
  const patron = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let desde = 0;
  let m;
  let n = 0;
  const llano = (t) => { if (t) piezas.push(<MathText key={`t${n += 1}`} content={t} />); };
  while ((m = patron.exec(texto)) !== null) {
    llano(texto.slice(desde, m.index));
    const pieza = m[0];
    if (pieza.startsWith('**')) {
      piezas.push(<strong key={`b${n += 1}`} className="font-semibold text-[#F4F4F3]"><MathText content={pieza.slice(2, -2)} /></strong>);
    } else {
      piezas.push(<code key={`c${n += 1}`} className="px-1 py-0.5 rounded-md bg-[#1B1B1B] border border-[#2C2C2C] text-[13px] font-mono">{pieza.slice(1, -1)}</code>);
    }
    desde = m.index + pieza.length;
  }
  llano(texto.slice(desde));
  return <>{piezas}</>;
}

export default function Marcado({ texto }) {
  const bloques = bloquesDe(texto);
  return (
    <Suspense fallback={<div className="min-w-0 whitespace-pre-wrap text-[15px] leading-[1.62] text-[#E8E8E6]" data-testid="tutor-marcado-llano">{texto}</div>}>
    <div className="min-w-0 grid gap-3 text-[15px] leading-[1.62] text-[#E8E8E6]" data-testid="tutor-marcado">
      {bloques.map((b, i) => {
        if (b.tipo === 'parrafo') {
          return (
            <p key={i} className={`m-0${i === 0 ? ' text-[#F4F4F3] font-medium' : ''}`} data-bloque={i === 0 ? 'lead' : 'parrafo'}>
              <EnLinea texto={b.texto} />
            </p>
          );
        }
        if (b.tipo === 'numerada') {
          return (
            <ol key={i} className="m-0 p-0 list-none grid gap-2.5" data-bloque="numerada">
              {b.items.map((item, j) => (
                <li key={j} className="grid grid-cols-[24px_1fr] gap-2.5 items-start">
                  <span aria-hidden="true" className="mt-0.5 w-6 h-6 rounded-full bg-[#241710] border border-[#3A2414] text-[#F7A06A] font-['Space_Grotesk'] font-bold text-[12px] flex items-center justify-center">{j + 1}</span>
                  <span className="min-w-0"><EnLinea texto={item} /></span>
                </li>
              ))}
            </ol>
          );
        }
        if (b.tipo === 'vinetas') {
          return (
            <ul key={i} className="m-0 p-0 list-none grid gap-2" data-bloque="vinetas">
              {b.items.map((item, j) => (
                <li key={j} className="grid grid-cols-[16px_1fr] gap-2 items-start">
                  <span aria-hidden="true" className="mt-[10px] w-1.5 h-1.5 rounded-full bg-[#9A9A9A]" />
                  <span className="min-w-0"><EnLinea texto={item} /></span>
                </li>
              ))}
            </ul>
          );
        }
        if (b.tipo === 'regla') {
          return (
            <div key={i} className="rounded-xl bg-[#241710] border border-[#3A2414] px-3.5 py-2.5 text-[14px] leading-[1.5] text-[#F7A06A]" data-bloque="regla">
              <EnLinea texto={b.texto} />
            </div>
          );
        }
        if (b.tipo === 'diagrama') {
          if (motivoDelSvg(b.svg)) return null;
          return (
            <div key={i} className="rounded-[14px] bg-[#101010] border border-[#1F1F1F] px-4 py-3.5" data-bloque="diagrama">
              {/* El modelo dibuja con tinta oscura sobre fondo transparente (medido en el banco): sobre la tarjeta
                  oscura no se leeria. El dibujo va sobre un recuadro claro DENTRO de la tarjeta (pregunta a Carlos). */}
              <div className="rounded-[10px] bg-[#F4F4F3] p-2" data-parte="lienzo">
                <img src={`data:image/svg+xml;utf8,${encodeURIComponent(svgParaPintar(b.svg))}`} alt={tituloDelSvg(b.svg)}
                  className="block w-full h-auto" data-testid="tutor-diagrama" />
              </div>
            </div>
          );
        }
        if (b.tipo === 'nota') {
          return (
            <div key={i} className="flex gap-2 items-start text-[13px] leading-[1.45] text-[#9A9A9A]" data-bloque="nota">
              <Info className="w-3.5 h-3.5 shrink-0 mt-[3px]" aria-hidden="true" />
              <span className="min-w-0"><EnLinea texto={b.texto} /></span>
            </div>
          );
        }
        return (
          <div key={i} className="rounded-[14px] bg-[#101010] border border-[#1F1F1F] px-4 py-3.5 text-center text-[17px] text-[#F4F4F3] overflow-x-auto" data-bloque="formula">
            <MathText content={b.texto} />
          </div>
        );
      })}
    </div>
    </Suspense>
  );
}
